import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import Header from './Header';
import FileList from './FileList';
import Breadcrumb from './Breadcrumb';
import apiService from '../services/apiService';

const Dashboard = () => {
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentFolder, setCurrentFolder] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentSortOption, setCurrentSortOption] = useState('name-asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20); // Files per page - using a smaller value than 50 to make pagination more visible
  const [folderItemsPerPage] = useState(10); // Folders per page
  const [hasSubfolders, setHasSubfolders] = useState(false);
  
  // Fetch folder structure first, then conditionally fetch files
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setCurrentPage(1); // Reset to first page when fetching new data
    
    try {
      // First fetch folders structure for the current folder
      const foldersData = await apiService.fetchFolderStructure(currentFolder);
      const subfolders = foldersData.folders || [];
      setFolders(subfolders);
      
      // Check if there are subfolders in the current folder
      const foldersInCurrentFolder = subfolders.filter(folder => 
        folder.name.startsWith(currentFolder) && folder.name !== currentFolder
      );
      
      setHasSubfolders(foldersInCurrentFolder.length > 0);
      
      // Only fetch files if there are no subfolders
      if (foldersInCurrentFolder.length === 0) {
        const filesData = await apiService.fetchFilesForFolder(currentFolder);
        setFiles(filesData.files || []);
      } else {
        // Clear files array if we're showing folders
        setFiles([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [currentFolder]);
  
  // Initial data load
  useEffect(() => {
    fetchData();
    
    // Set up automatic refresh every 5 minutes
    const refreshInterval = setInterval(() => {
      fetchData();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(refreshInterval);
  }, [fetchData]);
  
  // Handle folder click
  const handleFolderClick = (folderPath) => {
    setCurrentFolder(folderPath);
  };
  
  // Handle search
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };
  
  // Handle sort change
  const handleSortChange = (e) => {
    setCurrentSortOption(e.target.value);
  };
  
  // Handle CSV export
  const handleExportCSV = async () => {
    try {
      console.log('Exporting CSV for folder:', currentFolder, 'page:', currentPage, 'items per page:', effectiveItemsPerPage);
      // Show loading indication
      setIsLoading(true);
      
      // Pass current pagination settings to the export function
      await apiService.exportFilesAsCsv(currentFolder, currentPage, effectiveItemsPerPage);
      
      // Success notification could be added here if needed
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setError('Failed to export CSV. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Filter files based on search query
  const filteredFiles = files.filter(file => {
    if (!searchQuery) return true;
    const fileName = file.name.split('/').pop().toLowerCase();
    return fileName.includes(searchQuery.toLowerCase());
  });
  
  // Filter folders based on search query
  const filteredFolders = folders.filter(folder => {
    if (!searchQuery) return true;
    const folderName = folder.name.split('/').pop().toLowerCase();
    return folderName.includes(searchQuery.toLowerCase());
  });
  
  // Calculate total items for pagination (either folders or files, not both)
  const totalItems = hasSubfolders ? filteredFolders.length : filteredFiles.length;
  const effectiveItemsPerPage = hasSubfolders ? folderItemsPerPage : itemsPerPage;
  const totalPages = Math.ceil(totalItems / effectiveItemsPerPage);
  
  // Log pagination details to debug
  console.log('Pagination details:', {
    totalItems,
    effectiveItemsPerPage,
    totalPages,
    currentPage,
    hasSubfolders,
    itemCount: hasSubfolders ? filteredFolders.length : filteredFiles.length
  });
  
  // Handle page change
  const handlePageChange = (newPage) => {
    console.log(`Changing page from ${currentPage} to ${newPage}`);
    
    // Stop all audio playback when changing pages
    if (window._audioPlayerInstances) {
      window._audioPlayerInstances.forEach(closeFunc => {
        if (typeof closeFunc === 'function') {
          closeFunc();
        }
      });
    }
    
    setCurrentPage(newPage);
  };
  
  // Handle sort change from table header
  const handleSortColumn = (sortKey) => {
    setCurrentSortOption(sortKey);
  };
  
  return (
    <DashboardContainer>
      <Header />
      
      <BucketContainer>
        <BucketHeader>
          <BucketTitle>
            <i className="fas fa-folder-open"></i>
            <span>HCI Call Recording Data</span>
          </BucketTitle>
          
          <BucketControls>
            <SearchInput
              type="text"
              placeholder="Search files..."
              value={searchQuery}
              onChange={handleSearch}
            />
            <ExportButton onClick={handleExportCSV}>
              <i className="fas fa-file-csv"></i> CSV
            </ExportButton>
          </BucketControls>
        </BucketHeader>
        
        {error && (
          <ErrorMessage>
            <i className="fas fa-exclamation-circle"></i>
            <span>{error}</span>
            <RetryButton onClick={fetchData}>
              <i className="fas fa-redo"></i> Retry
            </RetryButton>
          </ErrorMessage>
        )}
        
        <Breadcrumb 
          currentFolder={currentFolder} 
          onFolderClick={handleFolderClick} 
        />
        
        {searchQuery && (
          <SearchIndicator>
            <i className="fas fa-search"></i>
            <span>Searching for: <strong>{searchQuery}</strong></span>
            <ClearSearchButton onClick={() => setSearchQuery('')}>
              <i className="fas fa-times"></i> Clear
            </ClearSearchButton>
          </SearchIndicator>
        )}
        
        {!isLoading && !error && (
          <ItemCount>
            Showing {Math.min((currentPage - 1) * effectiveItemsPerPage + 1, totalItems)}-
            {Math.min(currentPage * effectiveItemsPerPage, totalItems)} of {totalItems} 
            {hasSubfolders ? ' folders' : ' files'}
            {searchQuery ? ' matching search' : ''}
          </ItemCount>
        )}
        
        <FileList
          folders={hasSubfolders ? filteredFolders : []}
          files={hasSubfolders ? [] : filteredFiles}
          isLoading={isLoading}
          currentFolder={currentFolder}
          onFolderClick={handleFolderClick}
          sortOption={currentSortOption}
          currentPage={currentPage}
          itemsPerPage={effectiveItemsPerPage}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          showFoldersOnly={hasSubfolders}
          onSortChange={handleSortColumn}
          currentSortOption={currentSortOption}
        />
      </BucketContainer>
    </DashboardContainer>
  );
};

// Styled Components
const DashboardContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 30px;
  position: relative;
  min-height: 100vh;
`;

const BucketContainer = styled.div`
  background: var(--glass-bg);
  border-radius: 16px;
  box-shadow: var(--shadow);
  padding: 20px;
  backdrop-filter: blur(10px);
  border: 1px solid var(--glass-border);
  animation: fadeIn 0.3s ease;
`;

const BucketHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
  }
`;

const BucketTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 20px;
  font-weight: 600;
  color: var(--primary-color);
  
  i {
    font-size: 18px;
  }
`;

const BucketControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  
  @media (max-width: 576px) {
    flex-wrap: wrap;
  }
`;

const SearchInput = styled.input`
  padding: 10px 15px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 14px;
  min-width: 200px;
  transition: var(--transition);
  
  &:focus {
    outline: none;
    border-color: var(--secondary-color);
    box-shadow: 0 0 0 3px rgba(0, 160, 233, 0.1);
  }
  
  @media (max-width: 576px) {
    flex: 1;
    min-width: 0;
  }
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 15px;
  background-color: #002c5f;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background-color: #001a36;
  }
  i {
    font-size: 16px;
  }
`;

const ErrorMessage = styled.div`
  padding: 10px 15px;
  margin-bottom: 15px;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 8px;
  color: var(--text-light);
  font-size: 14px;
  display: flex;
  align-items: center;
  border: 1px solid var(--glass-border);
`;

const RetryButton = styled.button`
  padding: 10px 15px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-light);
  background-color: var(--secondary-color);
  transition: var(--transition);
  
  &:hover {
    background-color: var(--secondary-hover);
  }
`;

const SearchIndicator = styled.div`
  padding: 10px 15px;
  margin-bottom: 15px;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 8px;
  color: var(--text-light);
  font-size: 14px;
  display: flex;
  align-items: center;
  border: 1px solid var(--glass-border);
  
  i {
    margin-right: 8px;
    color: var(--secondary-color);
  }
  
  span {
    flex: 1;
  }
`;

const ClearSearchButton = styled.button`
  margin-left: auto;
  padding: 8px 15px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  color: white;
  background-color: #002c5f;
  transition: var(--transition);
  display: flex;
  align-items: center;
  gap: 6px;
  
  i {
    font-size: 12px;
    margin-right: 0;
    color: white;
  }
  
  &:hover {
    background-color: #001a36;
    transform: translateY(-1px);
  }
`;

const ItemCount = styled.div`
  padding: 10px 15px;
  margin-bottom: 15px;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 8px;
  color: var(--text-light);
  font-size: 14px;
  display: flex;
  align-items: center;
  border: 1px solid var(--glass-border);
`;

export default Dashboard;